// src/components/ProjectMarkers.js

import React, { memo, useEffect, useRef } from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { offsetMarkerPosition, getMarkerIcon } from '../utils/utils';
import ProjectPopup from './ProjectPopup';
import { incrementProjectViews } from '../services/api';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

export const ProjectMarkers = memo(
  ({ projects, handleMarkerClick, selectedProject, handleMoreInfoClick, isLoadingDetails }) => {
    const map = useMap();
    const markerRefs = useRef({});
    const clusterGroupRef = useRef(null);
    const popupRefs = useRef({});
    const lastSelectedId = useRef(null);

    // Effet pour gérer l'ouverture du popup du projet sélectionné
    useEffect(() => {
      if (!map) return;

      // Gérer l'ouverture/fermeture des popups
      if (selectedProject) {
        const marker = markerRefs.current[selectedProject.id];
        const popup = popupRefs.current[selectedProject.id];
        
        if (marker && popup) {
          // Attendre que l'animation soit terminée avant d'ouvrir la popup
          const openPopup = () => {
            popup.openOn(map);
            lastSelectedId.current = selectedProject.id;
            // Réinitialiser lastSelectedId après un délai
            setTimeout(() => {
              lastSelectedId.current = null;
            }, 500);
          };

          if (map._isAnimating) {
            map.once('flyend', openPopup);
          } else {
            openPopup();
          }
        }
      } else {
        // Fermer toutes les popups
        Object.values(markerRefs.current).forEach(marker => {
          marker.closePopup();
        });
        lastSelectedId.current = null;
      }
    }, [selectedProject, map]);

    // Effet pour fermer les popups lors du dézoom
    useEffect(() => {
      const closePopupsOnZoom = () => {
        // Ne fermer le popup que si on n'est pas en train de zoomer vers un marqueur
        const isZoomingToMarker = lastSelectedId.current !== null;
        if (map.getZoom() < 13 && !isZoomingToMarker) {
          map.closePopup();
          lastSelectedId.current = null;
        }
      };

      map.on('zoomend', closePopupsOnZoom);
      return () => map.off('zoomend', closePopupsOnZoom);
    }, [map]);

    const handleMarkerClickWithView = async (projectId, e) => {
      e.originalEvent.stopPropagation();
      const map = e.target._map;
      const currentZoom = map.getZoom();
      const targetZoom = currentZoom < 16 ? 16 : currentZoom;
      
      // Vérifier si le popup est déjà ouvert pour ce projet
      const isCurrentlySelected = selectedProject && selectedProject.id === projectId;
      
      if (!isCurrentlySelected) {
        try {
          // Incrémenter le compteur de vues seulement si on ouvre un nouveau popup
          await incrementProjectViews(projectId);
        } catch (error) {
          console.error('Erreur lors de l\'incrémentation des vues:', error);
        }
      }
      
      // Mettre à jour le projet sélectionné (null si on ferme le même projet)
      handleMarkerClick(isCurrentlySelected ? null : projectId);
      
      // Centrer et zoomer avec une animation plus douce seulement si on ouvre un nouveau popup
      if (!isCurrentlySelected) {
        map.flyTo(e.target.getLatLng(), targetZoom, {
          duration: 1.5,
          easeLinearity: 0.25
        });
      }
    };

    return (
      <MarkerClusterGroup
        ref={clusterGroupRef}
        spiderfyOnMaxZoom={true}
        maxClusterRadius={50}
        disableClusteringAtZoom={13}
        showCoverageOnHover={false}
        animate={true}
        animateAddingMarkers={true}
        onClusterClick={(e) => {
          const map = e.target._map;
          map.setView(e.latlng, map.getMaxZoom());
        }}
      >
        {projects.map((project, index) => (
          <Marker
            key={`${project.id}-${index}`}
            position={offsetMarkerPosition(
              project.latitude,
              project.longitude,
              index,
              projects.length
            )}
            icon={getMarkerIcon(project)}
            ref={(ref) => {
              if (ref) {
                markerRefs.current[project.id] = ref;
              }
            }}
            eventHandlers={{
              click: (e) => handleMarkerClickWithView(project.id, e),
            }}
            data-project-id={project.id}
          >
            <Popup
              ref={(ref) => {
                if (ref) {
                  popupRefs.current[project.id] = ref;
                }
              }}
              closeButton={false}
              autoPan={true}
              autoPanPadding={[50, 50]}
              className="project-marker-popup"
            >
              <ProjectPopup
                project={selectedProject && selectedProject.id === project.id ? selectedProject : project}
                isLoading={isLoadingDetails && selectedProject && selectedProject.id === project.id}
              />
            </Popup>
          </Marker>
        ))}
      </MarkerClusterGroup>
    );
  }
);